/**
 * Woodcrafy motion layer — lightweight, dependency-free interactions for the redesign sections.
 *
 * Declarative hooks (all optional, all progressive):
 *  [data-wc-reveal]          Fade/slide in when scrolled into view. Stagger with style="--wc-delay: .1s".
 *  [data-wc-split]           Word-by-word rise for headings rendered by snippets/wc-split-words.liquid.
 *  [data-wc-count]           Count up the first number in the element's text when it enters the view.
 *  [data-wc-deal]            Gets .is-in when in view (used by the reviews "dealt cards" entrance).
 *  [data-wc-magnetic]        Element drifts toward the pointer (fine pointers only).
 *  [data-wc-tilt]            Child [data-wc-tilt-target] follows the pointer inside this element.
 *  [data-wc-marquee]         Scroll-velocity aware ticker (falls back to the CSS animation).
 *  [data-wc-scrub]           Scroll-linked word fade ([data-wc-scrub-word]) and parallax ([data-wc-parallax]).
 *  header-component.header--floating  Gets .is-scrolled once the page scrolls.
 *
 * Scroll position comes from @theme/scroll-container: on desktop this theme scrolls inside
 * .page-wrapper (html/body don't scroll), on mobile the document scrolls.
 *
 * Hidden "before" states are scoped under html.wc-motion, which is only added here, so content is
 * always visible without JavaScript. Everything is skipped for prefers-reduced-motion.
 */

import { getScrollTop } from '@theme/scroll-container';

const root = document.documentElement;
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const EASE_OUT_EXPO = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

/** @type {WeakSet<Element>} */
const bound = new WeakSet();

function once(element) {
  if (bound.has(element)) return false;
  bound.add(element);
  return true;
}

/* ---------------------------------------------------------------- reveal + split + count */

const revealObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const element = entry.target;
      element.classList.add('is-in');
      revealObserver.unobserve(element);
      if (element instanceof HTMLElement && element.hasAttribute('data-wc-count')) countUp(element);
      for (const counter of element.querySelectorAll('[data-wc-count]')) {
        if (counter instanceof HTMLElement) countUp(counter);
      }
    }
  },
  { rootMargin: '0px 0px -12% 0px', threshold: 0 }
);

/** @param {HTMLElement} element */
function prepareCount(element) {
  if (element.dataset.wcCountReady) return;
  const text = element.textContent ?? '';
  const match = text.match(/^(\D*?)(\d[\d,.]*)(.*)$/s);
  if (!match) return;
  const [, prefix, number, suffix] = match;
  const target = Number(number.replace(/,/g, ''));
  if (!Number.isFinite(target)) return;
  element.dataset.wcCountReady = 'true';
  element.dataset.wcCountPrefix = prefix;
  element.dataset.wcCountSuffix = suffix;
  element.dataset.wcCountTarget = String(target);
  element.dataset.wcCountGrouping = number.includes(',') ? 'true' : '';
  element.style.fontVariantNumeric = 'tabular-nums';
  element.setAttribute('aria-label', text.trim());
  write(element, 0);
}

/** @param {HTMLElement} element @param {number} value */
function write(element, value) {
  const grouped = element.dataset.wcCountGrouping
    ? Math.round(value).toLocaleString('en-US')
    : String(Math.round(value));
  element.textContent = `${element.dataset.wcCountPrefix ?? ''}${grouped}${element.dataset.wcCountSuffix ?? ''}`;
}

/** @param {HTMLElement} element */
function countUp(element) {
  if (!element.dataset.wcCountReady) return;
  const target = Number(element.dataset.wcCountTarget);
  const duration = Number(element.dataset.wcCount) || 1400;
  const start = performance.now();
  const tick = (now) => {
    const progress = Math.min(1, (now - start) / duration);
    write(element, target * EASE_OUT_EXPO(progress));
    if (progress < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

// Starts image loading for deferred backgrounds (see wc-product-card) well before cards scroll in.
const nearObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.setAttribute('data-wc-near', '');
      nearObserver.unobserve(entry.target);
    }
  },
  { rootMargin: '600px 0px 600px 0px' }
);

function initReveal(scope) {
  for (const element of scope.querySelectorAll('[data-wc-reveal]')) nearObserver.observe(element);
  for (const element of scope.querySelectorAll('[data-wc-reveal], [data-wc-split], [data-wc-count], [data-wc-deal]')) {
    if (!once(element)) continue;
    if (element instanceof HTMLElement && element.hasAttribute('data-wc-count')) prepareCount(element);
    revealObserver.observe(element);
  }
}

/* ---------------------------------------------------------------- pointer: magnetic + tilt */

function follow(element, strength, { onMove, onReset }) {
  let targetX = 0;
  let targetY = 0;
  let x = 0;
  let y = 0;
  let frame = 0;

  const loop = () => {
    x += (targetX - x) * 0.18;
    y += (targetY - y) * 0.18;
    onMove(x, y);
    if (Math.abs(targetX - x) > 0.1 || Math.abs(targetY - y) > 0.1) {
      frame = requestAnimationFrame(loop);
    } else {
      frame = 0;
      if (targetX === 0 && targetY === 0) onReset();
    }
  };
  const kick = () => {
    if (!frame) frame = requestAnimationFrame(loop);
  };

  element.addEventListener('pointermove', (event) => {
    if (event.pointerType !== 'mouse') return;
    const rect = element.getBoundingClientRect();
    [targetX, targetY] = strength(event, rect);
    kick();
  });
  element.addEventListener('pointerleave', () => {
    targetX = 0;
    targetY = 0;
    kick();
  });
}

function initPointer(scope) {
  if (!finePointer.matches) return;

  for (const element of scope.querySelectorAll('[data-wc-magnetic]')) {
    if (!(element instanceof HTMLElement) || !once(element)) continue;
    const factor = Number(element.dataset.wcMagnetic) || 0.25;
    follow(element, (event, rect) => [
      (event.clientX - rect.left - rect.width / 2) * factor,
      (event.clientY - rect.top - rect.height / 2) * factor,
    ], {
      onMove: (x, y) => (element.style.translate = `${x}px ${y}px`),
      onReset: () => (element.style.translate = ''),
    });
  }

  for (const element of scope.querySelectorAll('[data-wc-tilt]')) {
    if (!(element instanceof HTMLElement) || !once(element)) continue;
    const target = element.querySelector('[data-wc-tilt-target]');
    if (!(target instanceof HTMLElement)) continue;
    const range = Number(element.dataset.wcTilt) || 26;
    follow(element, (event, rect) => [
      ((event.clientX - rect.left) / rect.width - 0.5) * range,
      ((event.clientY - rect.top) / rect.height - 0.5) * range,
    ], {
      onMove: (x, y) => (target.style.translate = `${x}px ${y}px`),
      onReset: () => (target.style.translate = ''),
    });
  }
}

/* ---------------------------------------------------------------- scroll: velocity, scrub, header */

let scrollVelocity = 0;
let lastScrollY = getScrollTop();
let lastScrollTime = performance.now();
/** @type {Set<() => void>} */
const scrollSubscribers = new Set();
let scrollFrame = 0;

// Scroll events don't bubble, so capture them at the document: this catches the window and
// .page-wrapper alike, whichever is the active scroll container.
document.addEventListener(
  'scroll',
  () => {
    const now = performance.now();
    const delta = now - lastScrollTime;
    const scrollY = getScrollTop();
    if (delta > 0) {
      const instant = ((scrollY - lastScrollY) / delta) * 1000;
      scrollVelocity += (instant - scrollVelocity) * 0.25;
    }
    lastScrollY = scrollY;
    lastScrollTime = now;
    if (!scrollFrame) {
      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = 0;
        for (const subscriber of scrollSubscribers) subscriber();
      });
    }
  },
  { passive: true, capture: true }
);

function initMarquee(scope) {
  for (const element of scope.querySelectorAll('[data-wc-marquee]')) {
    if (!(element instanceof HTMLElement) || !once(element)) continue;
    const track = element.querySelector('[data-wc-marquee-track]');
    if (!(track instanceof HTMLElement)) continue;

    const baseSpeed = Number(element.dataset.wcMarquee) || 2.2; // % of the doubled track per second
    let offset = 0;
    let direction = 1;
    let last = performance.now();
    let visible = false;
    let frame = 0;

    element.setAttribute('data-wc-marquee-js', '');

    const loop = (now) => {
      const dt = Math.min(64, now - last) / 1000;
      last = now;
      scrollVelocity *= 0.94; // decay when the page stops scrolling
      const boost = Math.max(-4, Math.min(4, scrollVelocity / 300));
      if (boost < -0.05) direction = -1;
      else if (boost > 0.05) direction = 1;
      if (!element.hasAttribute('data-paused')) {
        offset -= direction * (baseSpeed + Math.abs(boost) * baseSpeed) * dt;
        offset = ((offset % 50) - 50) % 50; // wrap into (-50, 0]
        track.style.transform = `translate3d(${offset}%, 0, 0)`;
      }
      frame = visible ? requestAnimationFrame(loop) : 0;
    };

    new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible && !frame) {
        last = performance.now();
        frame = requestAnimationFrame(loop);
      }
    }).observe(element);
  }
}

function initScrub(scope) {
  for (const element of scope.querySelectorAll('[data-wc-scrub]')) {
    if (!(element instanceof HTMLElement) || !once(element)) continue;
    const words = [...element.querySelectorAll('[data-wc-scrub-word]')];
    const parallax = element.querySelector('[data-wc-parallax]');
    let active = false;

    const update = () => {
      if (!active) return;
      const rect = element.getBoundingClientRect();
      const vh = window.innerHeight;
      // Word fade: starts when the top hits 85% of the viewport, ends when the bottom reaches 40%.
      const fade = Math.max(0, Math.min(1, (vh * 0.85 - rect.top) / (vh * 0.45 + rect.height)));
      words.forEach((word, index) => {
        const from = index / words.length;
        const to = (index + 1) / words.length;
        const local = Math.max(0, Math.min(1, (fade - from) / (to - from)));
        /** @type {HTMLElement} */ (word).style.opacity = String(0.18 + local * 0.82);
      });
      if (parallax instanceof HTMLElement) {
        const travel = Math.max(0, Math.min(1, (vh - rect.top) / (vh + rect.height)));
        parallax.style.transform = `translate3d(0, ${-8 + travel * 16}%, 0)`;
      }
    };

    new IntersectionObserver(([entry]) => {
      active = entry.isIntersecting;
      if (active) update();
    }).observe(element);
    scrollSubscribers.add(update);
    update();
  }
}

function initHeader() {
  const header = document.querySelector('header-component.header--floating');
  if (!header || !once(header)) return;
  const update = () => header.classList.toggle('is-scrolled', getScrollTop() > 12);
  scrollSubscribers.add(update);
  update();
  initNavHover(header);
}

/**
 * Desktop nav: one hover pill that slides between top-level links (the demo's shared-layout pill).
 * Hidden over the current page's link, which already has a dark pill.
 * @param {Element} header
 */
function initNavHover(header) {
  const columns = header.querySelector('.header__row--top .header__columns');
  const menu = header.querySelector('header-menu');
  if (!(columns instanceof HTMLElement) || !menu) return;

  const pill = document.createElement('span');
  pill.className = 'wc-nav-hover';
  pill.setAttribute('aria-hidden', 'true');
  columns.append(pill);
  let visible = false;

  menu.addEventListener('pointerover', (event) => {
    const item = event.target instanceof Element ? event.target.closest('.menu-list__list-item') : null;
    const title = item?.querySelector('.menu-list__link-title');
    if (!item || item.getAttribute('slot') === 'overflow' || !(title instanceof HTMLElement)) return;
    if (item.querySelector('.menu-list__link--active')) {
      pill.style.opacity = '0';
      visible = false;
      return;
    }
    const box = title.getBoundingClientRect();
    const base = columns.getBoundingClientRect();
    if (!visible) pill.style.transition = 'opacity 0.2s ease'; // appear in place, then slide
    pill.style.width = `${box.width}px`;
    pill.style.height = `${box.height}px`;
    pill.style.transform = `translate(${box.left - base.left}px, ${box.top - base.top}px)`;
    pill.style.opacity = '1';
    if (!visible) requestAnimationFrame(() => requestAnimationFrame(() => (pill.style.transition = '')));
    visible = true;
  });

  menu.addEventListener('pointerleave', () => {
    pill.style.opacity = '0';
    visible = false;
  });
}

/* ---------------------------------------------------------------- boot */

function init(scope = document) {
  initHeader();
  if (reduceMotion.matches) return;
  initReveal(scope);
  initPointer(scope);
  initMarquee(scope);
  initScrub(scope);
}

if (!reduceMotion.matches) root.classList.add('wc-motion');
init();


// Re-initialise sections added or re-rendered in the theme editor.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) init(event.target);
});

export { init };
